/**
 * Create-client screen (the operator's tenant surface).
 *
 * This is the page that closes ADR-0052's gap 2: before it, an `Client`
 * was constructible only from `db/seed/dev.py`, so onboarding a customer
 * meant an SSM session and a Python REPL.
 *
 * English-only by design: internal tooling, never a student-facing
 * surface, so it does not participate in lesson-content review.
 */
import { Notice } from '@speaking-coach/ui'
import { PageHeader } from '../components/page-header'
import { OPERATOR_FORM_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'
import { useRef } from 'react'
import { SubmitButton } from '../common/save-button'
import { ProvisioningFormActions } from '../common/provisioning-form-actions'
import { DEFAULT_TROPHY_GOAL } from '../common/product-defaults'
import {
  UnsavedChangesGuard,
  useLeaveAfterSave,
} from '../common/unsaved-changes'
import { adminClientsApi, messageForClientError } from './api'
import { createClientSchema, type CreateClientForm } from './client-schema'
import {
  InternalField,
  CompanyInformationSetting,
  NameField,
  SlugField,
  TrophyTargetField,
} from './client-form-fields'

/**
 * Stable 53-bit suffix for a name that has no safe ASCII handle.
 *
 * Kanji cannot be romanized reliably without knowing its reading. A compact
 * deterministic handle is more honest than guessing, and much less collision
 * prone than truncating code points. The operator can still replace it with a
 * preferred romaji slug before the first student activates.
 */
function nonAsciiSlugSuffix(value: string): string {
  let first = 0xdeadbeef ^ value.length
  let second = 0x41c6ce57 ^ value.length
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    first = Math.imul(first ^ code, 2_654_435_761)
    second = Math.imul(second ^ code, 1_597_334_677)
  }
  first =
    Math.imul(first ^ (first >>> 16), 2_246_822_507) ^
    Math.imul(second ^ (second >>> 13), 3_266_489_909)
  second =
    Math.imul(second ^ (second >>> 16), 2_246_822_507) ^
    Math.imul(first ^ (first >>> 13), 3_266_489_909)
  const hash = 4_294_967_296 * (2_097_151 & second) + (first >>> 0)
  return hash.toString(36)
}

/** `Acme Manufacturing KK` → `acme-manufacturing-kk`. */
export function slugify(name: string): string {
  const normalizedName = name.normalize('NFKC')
  const asciiSlug = normalizedName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (asciiSlug !== '') return asciiSlug
  if (normalizedName.trim() === '') return ''
  return `client-${nonAsciiSlugSuffix(normalizedName)}`
}

export function CreateClientPage(): JSX.Element {
  // Success navigation goes through `leave` so the unsaved-changes guard is
  // provably stood down first — see `useLeaveAfterSave`.
  const { leaving, leave } = useLeaveAfterSave()
  const queryClient = useQueryClient()

  // Validate while editing so the primary action reflects whether the form
  // can actually be submitted. RHF still starts with an empty error object,
  // so a pristine form remains quiet; an invalid value explains itself at
  // the field instead of producing a click that appears to do nothing.
  const form = useForm<CreateClientForm>({
    resolver: zodResolver(createClientSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      slug: '',
      contractTrophyTarget: DEFAULT_TROPHY_GOAL,
      isInternal: false,
      clientEmployeeIdEnabled: false,
      clientDepartmentEnabled: false,
    },
  })
  const { control, handleSubmit, setValue, formState } = form
  const errors = formState.errors

  // The slug auto-fills from the name until the operator edits it by hand.
  // Do not infer that from RHF's dirty state: a programmatic `setValue` also
  // becomes dirty once validation runs, which used to freeze the generated
  // slug after the second character of the name.
  const slugEditedByOperator = useRef(false)

  const create = useMutation({
    mutationFn: (values: CreateClientForm) =>
      adminClientsApi.create({
        name: values.name.trim(),
        slug: values.slug.trim(),
        contractTrophyTarget: values.contractTrophyTarget,
        isInternal: values.isInternal,
        clientEmployeeIdEnabled: values.clientEmployeeIdEnabled,
        clientDepartmentEnabled: values.clientDepartmentEnabled,
      }),
    onSuccess: (created) => {
      // Seed the detail so the page we land on paints from cache rather
      // than re-fetching what the POST just returned.
      queryClient.setQueryData(queryKeys.client(created.clientId), created)
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientLists })
      // Straight to the client page — the next thing an operator does
      // is add students to it.
      leave(`/admin/clients/${created.clientId}`)
    },
  })

  const submitting = create.isPending
  const error =
    create.error !== null ? messageForClientError(create.error) : null

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_FORM_PAGE}>
        <PageHeader title="New client" />

        <section className="bg-surface-card rounded-token-card p-6">
          <form
            onSubmit={handleSubmit((values) => {
              if (!formState.isValid || create.isPending) return
              create.mutate(values)
            })}
            className="space-y-4"
            aria-label="New client"
            noValidate
          >
            {/* `noValidate`: the zod schema is the authority, and native
                constraint validation would pre-empt it — the browser's own
                bubble appears instead of our wording, and in jsdom it blocks
                submission outright so `handleSubmit` never runs. That is what
                made these messages look like a validation bug rather than the
                form never being submitted. The `required` attributes stay for
                assistive tech. */}
            {/* Radix primitives are controlled, so they go through
                `Controller`; the plain inputs could be uncontrolled but stay
                controlled here because the slug field is DERIVED from the name
                and has to be written to. */}
            <Notice variant="neutral">
              <p className="font-medium text-text-primary">
                What does this create?
              </p>
              <p className="mt-0.5">
                The client only. Add students afterwards; cohort start and end
                dates are configured on each cohort.
              </p>
            </Notice>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <NameField
                  value={field.value}
                  error={errors.name?.message ?? null}
                  onChange={(next) => {
                    field.onChange(next)
                    if (!slugEditedByOperator.current) {
                      setValue('slug', slugify(next), { shouldValidate: true })
                    }
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="slug"
              render={({ field }) => (
                <SlugField
                  value={field.value}
                  error={errors.slug?.message ?? null}
                  onChange={(next) => {
                    slugEditedByOperator.current = true
                    field.onChange(next)
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="contractTrophyTarget"
              render={({ field }) => (
                <TrophyTargetField
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.contractTrophyTarget?.message ?? null}
                />
              )}
            />
            <fieldset className="border-b border-border-default pb-4">
              <legend className="font-medium">Company information</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <Controller
                  control={control}
                  name="clientEmployeeIdEnabled"
                  render={({ field }) => (
                    <CompanyInformationSetting field="client-employee-enabled" label="Employee ID" value={field.value ?? false} onChange={field.onChange} />
                  )}
                />
                <Controller
                  control={control}
                  name="clientDepartmentEnabled"
                  render={({ field }) => (
                    <CompanyInformationSetting field="client-department-enabled" label="Department" value={field.value ?? false} onChange={field.onChange} />
                  )}
                />
              </div>
            </fieldset>
        <Controller
              control={control}
              name="isInternal"
              render={({ field }) => (
                <InternalField value={field.value} onChange={field.onChange} />
              )}
            />

            {error && (
              <Notice variant="danger">
                {error}
              </Notice>
            )}

            <ProvisioningFormActions cancelTo="/admin/clients">
              <SubmitButton
                pending={submitting}
                pendingLabel="Creating…"
                disabled={!formState.isValid}
              >
                Create client
              </SubmitButton>
            </ProvisioningFormActions>
          </form>
        </section>

        <UnsavedChangesGuard
          when={formState.isDirty && !leaving}
          name="the new client"
        />
      </div>
    </main>
  )
}
